import { Autorenew, AutorenewOutlined, AutorenewRounded, AutorenewSharp, AutorenewTwoTone } from "@mui/icons-material";
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { Stack } from "@mui/system";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { FormContainer, PasswordElement, TextFieldElement, useForm } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";

import { CreateUserRequestDto } from "@/shared/api";

const CreateUserModal = ({
  open,
  onClose,
  onSave,
  isLoading = false,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateUserRequestDto) => void;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const form = useForm<CreateUserRequestDto>({
    defaultValues: { username: "", password: "" },
  });

  const onCloseHandler = () => {
    form.reset();
    onClose();
  };

  useEffect(() => {
    form.reset();
  }, [open, form]);

  function handleGeneratePasswordClick(): void {
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const specials = '!@#$%^&*()_-+=<>[]{}|~.:';
    const all = lower + upper + digits + specials;
    const length = 16;

    const get = (chars: string) => chars[Math.floor(Math.random() * chars.length)];
    const password = [
      get(lower), get(upper), get(digits), get(specials),
      ...Array.from({ length: length - 4 }, () => get(all))
    ];

    const newPassword = password.sort(() => Math.random() - 0.5).join('');

    form.setValue("password", newPassword);
    navigator.clipboard
      .writeText(newPassword)
      .then(() =>
        enqueueSnackbar(t("Generated password copied to clipboard"), { variant: "info" })
      );
  }

  return (
    <Dialog open={open} onClose={onCloseHandler} fullWidth maxWidth="sm">
      <DialogTitle title="Create User">{t("createUser")}</DialogTitle>
      <DialogContent style={{ paddingTop: "5px" }}>
        <FormContainer formContext={form} onSuccess={(val) => onSave(val)}>
          <Stack spacing={2}>
            <TextFieldElement name="username" label={t("username")} required fullWidth />
            <Box display="flex" alignItems="center" gap={1}>
              <PasswordElement name="password" label={t("password")} required fullWidth />

              <div>
                <IconButton
                  onClick={handleGeneratePasswordClick}
                  size="small"
                  sx={{
                    position: "absolute",
                    right: 83,          // сдвиг левее
                    top: "62,5%",         // сдвиг ниже
                    transform: "translateY(-50%)",
                    border: "1px solid #ccc",
                    width: 32,
                    height: 32,
                    zIndex: 1,
                    borderStyle: "none",
                  }}
                >
                  <Autorenew  fontSize="small" />
                </IconButton>
              </div>
            </Box>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={isLoading}
              startIcon={isLoading && <CircularProgress size={18} />}
            >
              {t("createUser")}
            </Button>
          </Stack>
        </FormContainer>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserModal;
